import {
  FacetQuizlet,
  COLOR_QUIZLETS,
  STYLE_QUIZLETS,
} from '~/pages/weddings/components/quizlets/FacetQuizlet/meta';
import { RootState } from '~/reducers';
import { IUserContext } from '~/types/userContext';

export const getQuizletAnswersLoaded = (state: RootState): boolean => {
  return state.ui.weddings.quizlets.loaded;
};

export const getQuizletAnswers = (state: RootState): Record<string, boolean> => {
  return state.ui.weddings.quizlets.answers;
};

export enum QuizType {
  LOCATION = 'LOCATION',
  COLOR = 'COLOR',
  STYLE = 'STYLE',
  STYLE_GROUP = 'STYLE_GROUP',
}

interface ColorQuiz {
  type: QuizType.COLOR;
  quizlet: FacetQuizlet;
}

interface LocationQuiz {
  type: QuizType.LOCATION;
}

interface StyleQuiz {
  type: QuizType.STYLE;
  quizlet: FacetQuizlet;
}

interface StyleQuizGroup {
  type: QuizType.STYLE_GROUP;
  quizlets: FacetQuizlet[];
}

export type Quiz = ColorQuiz | LocationQuiz | StyleQuiz | StyleQuizGroup;

type LocationQuizResult = {
  loaded: boolean;
  answered: boolean;
  city: string | null;
  stateProvince: string | null;
};

export const determineWeddingLocationFromUserContext = (
  userContext: IUserContext | null
): LocationQuizResult => {
  if (userContext) {
    const { user_role_account_weddings: weddingRole = [] } = userContext;
    const { city = null, state_province: stateProvince = null } = weddingRole[0]
      ? weddingRole[0]
      : {};

    const hasAddress = Boolean(city) && Boolean(stateProvince);

    return {
      loaded: true,
      answered: hasAddress,
      city: hasAddress && city ? city : null,
      stateProvince: hasAddress && stateProvince ? stateProvince : null,
    };
  }

  return {
    loaded: false,
    answered: false,
    city: null,
    stateProvince: null,
  };
};

interface FacetQuizletResult {
  loaded: boolean;
  colors: FacetQuizlet[];
  styles: FacetQuizlet[];
}

export const determineFacetQuizzes = (
  state: RootState,
  userContext: IUserContext | null
): FacetQuizletResult => {
  const quizletAnswersLoaded = getQuizletAnswersLoaded(state);
  const quizletAnswers = getQuizletAnswers(state);

  if (userContext) {
    if (userContext.is_guest) {
      return {
        loaded: true,
        colors: COLOR_QUIZLETS,
        styles: STYLE_QUIZLETS,
      };
    }

    if (quizletAnswersLoaded) {
      return {
        loaded: true,
        colors: COLOR_QUIZLETS.filter((quizlet) => quizletAnswers[quizlet.key] === undefined),
        styles: STYLE_QUIZLETS.filter((quizlet) => quizletAnswers[quizlet.key] === undefined),
      };
    }
  }

  return {
    loaded: false,
    colors: COLOR_QUIZLETS,
    styles: STYLE_QUIZLETS,
  };
};

const LOCATION_QUIZ: Quiz = {
  type: QuizType.LOCATION,
};

interface GetQuizzesResult {
  loaded: boolean;
  locationQuiz?: LocationQuiz;
  colorQuizzes: ColorQuiz[];
  styleQuizzes: StyleQuiz[];
}

// This is not shuffling or grouping, we're leaving that to wherever these are presented
export const getQuizzes = (
  state: RootState,
  userContext: IUserContext | null
): GetQuizzesResult => {
  const locationResult = determineWeddingLocationFromUserContext(userContext);
  const facetResult = determineFacetQuizzes(state, userContext);

  const colorQuizzes = facetResult.colors.map((quizlet): ColorQuiz => {
    return {
      type: QuizType.COLOR,
      quizlet,
    };
  });

  const styleQuizzes = facetResult.styles.map((quizlet): StyleQuiz => {
    return {
      type: QuizType.STYLE,
      quizlet,
    };
  });

  return {
    loaded: locationResult.loaded && facetResult.loaded,
    locationQuiz: locationResult.answered ? undefined : LOCATION_QUIZ,
    colorQuizzes,
    styleQuizzes,
  };
};

export const getPreauthAnswers = (state: RootState) => state.ui.weddings.quizlets.preAuthAnswers;
